import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { LuPlus } from 'react-icons/lu';
import { confirm } from '@/utils/confirm';
import Loading from '@/components/Loading';
import { Tag } from '@/components/Tag';
import useDeleteAdminImportRequest from '../../api/useDeleteAdminImportRequest';
import useFetchAdminIam from '../../api/useFetchAdminIam';
import useFetchAdminImportRequestItems from '../../api/useFetchAdminImportRequestItems';
import usePostAdminImportRequestDone from '../../api/usePostAdminImportRequestDone';
import usePostAdminImportRequestLoginFailed from '../../api/usePostAdminImportRequestLoginFailed';
import { AdminButton } from '../../components/AdminButton';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import { STATUS_LABELS, STATUS_TAG_COLORS } from '../../constants/status';

type PendingPageProps = {
  importRequestId: number;
  status: string;
  adminUserName: string | null;
  jobSourceId: number | null;
  jobSourceLoginId: string | null;
  jobSourceLoginUrl: string | null;
  jobSourceLoginPass: string | null;
};

export default function PendingPage({
  importRequestId,
  status,
  adminUserName,
  jobSourceId,
  jobSourceLoginId,
  jobSourceLoginUrl,
  jobSourceLoginPass,
}: PendingPageProps) {
  const navigate = useNavigate();

  const { data: currentUser } = useFetchAdminIam();
  const { data: items = [], isLoading: isItemsLoading } =
    useFetchAdminImportRequestItems(importRequestId);
  const { mutate: postDone, isPending: isDonePending } = usePostAdminImportRequestDone();
  const { mutate: postLoginFailed, isPending: isLoginFailedPending } =
    usePostAdminImportRequestLoginFailed();
  const { mutate: deleteRequest, isPending: isDeletePending } = useDeleteAdminImportRequest();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => (a.company_name ?? '').localeCompare(b.company_name ?? '', 'ja')),
    [items]
  );
  const allItemsDone = items.length > 0 && items.every((item) => item.status === 'done');

  const hasJobSourceInfo =
    jobSourceId != null ||
    jobSourceLoginId != null ||
    jobSourceLoginUrl != null ||
    jobSourceLoginPass != null;

  const handleDone = async () => {
    const confirmed = await confirm({
      title: 'リクエストを完了',
      message: '全ての項目が対応済みです。このインポートリクエストを完了にしますか？',
    });
    if (!confirmed) return;
    postDone(importRequestId, {
      onSuccess: () => {
        navigate({ to: '/admin', search: { tab: 'import_requests' } });
      },
    });
  };

  const handleLoginFailed = async () => {
    const confirmed = await confirm({
      title: 'ログイン失敗として記録',
      message: 'このリクエストを「ログインできない」として失敗にします。よろしいですか？',
    });
    if (!confirmed) return;
    postLoginFailed(importRequestId, {
      onSuccess: () => {
        navigate({ to: '/admin', search: { tab: 'import_requests' } });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <AdminBackLink to="/admin" search={{ tab: 'import_requests' }} />
      <div className="flex flex-col gap-3 rounded border border-solid border-border bg-white p-6">
        {hasJobSourceInfo && (
          <dl className="grid grid-cols-[auto_1fr] gap-4 text-sm">
            <dt className="text-sub">ステータス</dt>
            <dd>
              <Tag color={STATUS_TAG_COLORS[status] ?? 'gray'} size="sm">
                {STATUS_LABELS[status] ?? status}
              </Tag>
            </dd>
            <dt className="text-sub">担当者</dt>
            <dd className="text-main">{adminUserName ?? '—'}</dd>
            <dt className="text-sub">ログインID</dt>
            <dd className="text-main">
              <input
                type="text"
                readOnly
                value={jobSourceLoginId ?? ''}
                placeholder="未設定"
                className="w-full max-w-md rounded border border-solid border-border bg-gray-50 px-3 py-2 text-sm text-main read-only:cursor-default"
              />
            </dd>
            <dt className="text-sub">パスワード</dt>
            <dd className="text-main">
              <input
                type="text"
                readOnly
                value={jobSourceLoginPass ?? ''}
                placeholder="未設定"
                className="w-full max-w-md rounded border border-solid border-border bg-gray-50 px-3 py-2 text-sm text-main read-only:cursor-default"
              />
            </dd>
            {jobSourceLoginUrl != null && (
              <>
                <dt className="text-sub">ログインURL</dt>
                <dd className="text-main">
                  <a
                    href={jobSourceLoginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4188ff] underline hover:opacity-80"
                  >
                    {jobSourceLoginUrl}
                  </a>
                </dd>
              </>
            )}
          </dl>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleLoginFailed}
              disabled={isLoginFailedPending}
              className="rounded border border-solid border-border bg-white px-4 py-2 text-sm text-main hover:bg-gray-50 disabled:opacity-50"
            >
              {isLoginFailedPending ? '送信中...' : 'ログインできない'}
            </button>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await confirm({
                    title: 'リクエストをスキップ',
                    message: 'このリクエストをスキップします。よろしいですか？',
                  });
                  if (!confirmed) return;
                  deleteRequest(importRequestId, {
                    onSuccess: () => {
                      navigate({ to: '/admin', search: { tab: 'import_requests' } });
                    },
                  });
                }}
                disabled={isDeletePending}
                className="rounded border border-solid border-red-300 bg-white px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {isDeletePending ? 'スキップ中...' : 'スキップ'}
              </button>
            )}
          </div>
          <div>
            {status === 'pending' && allItemsDone && (
              <AdminButton
                label={isDonePending ? '送信中...' : 'リクエストを完了'}
                onClick={handleDone}
                disabled={isDonePending}
                size="sm"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 rounded border border-solid border-border bg-white">
        <div className="flex items-center justify-between px-4 pt-4">
          {status === 'pending' && (
            <AdminButton
              label="求人を追加する"
              icon={<LuPlus className="size-4" />}
              size="sm"
              onClick={() =>
                navigate({
                  to: '/admin',
                  search: { tab: 'import_requests', importRequestId, itemAction: 'new' },
                })
              }
            />
          )}
        </div>
        {isItemsLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <Loading />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-solid border-border bg-gray-50">
                  <th className="px-4 py-3 text-left font-bold text-sub">ステータス</th>
                  <th className="px-4 py-3 text-left font-bold text-sub">求人タイトル</th>
                  <th className="px-4 py-3 text-left font-bold text-sub">企業</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="border-b border-solid border-border px-4 py-6 text-center text-sub"
                    >
                      項目がありません
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item) => {
                    const navigateToItem = () =>
                      navigate({
                        to: '/admin',
                        search: {
                          tab: 'import_requests',
                          importRequestId,
                          itemId: item.id,
                        },
                      });
                    return (
                      <tr
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={navigateToItem}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigateToItem();
                          }
                        }}
                        className="cursor-pointer border-b border-solid border-border hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <Tag color={STATUS_TAG_COLORS[item.status] ?? 'gray'} size="sm">
                            {STATUS_LABELS[item.status] ?? item.status}
                          </Tag>
                        </td>
                        <td className="max-w-[240px] truncate px-4 py-3 text-main">
                          {item.outer_job_post_title ?? '—'}
                        </td>
                        <td className="max-w-[240px] truncate px-4 py-3 text-main">
                          {item.company_name ?? '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
